// services
import fileSystem from "../file-system";
import storageService from "../storage.service";

// utilities
import { fixImageEXIFOrientation } from "../../utilities/image";

// types
import { isRemoteFileRef } from "./types";
import { StorageReference, UploadTaskSnapshot } from "firebase/storage";

// internal types
type _FileList = { path: string; file: File };

// types
export type ProgressTracker = Record<
  string,
  { process: number; [s: string]: unknown }
>;

export function generateProcessObject(files: FileList): ProgressTracker {
  return Object.entries(files).reduce<ProgressTracker>(
    (fs, [path, file], key) => {
      fs[`${path}.${key}`] = {
        process: 0,
        path: `${path}.${key}`,
      };
      return fs;
    },
    {},
  );
}

export async function _uploadFile(
  baseRef: StorageReference,
  fileName: string,
  file: File,
  cb: (process: number, snapshot: UploadTaskSnapshot) => void,
): Promise<UploadTaskSnapshot> {
  const ref = storageService.ref(baseRef, `/${fileName}`);
  if (file.type.startsWith("image/")) {
    const corrected = await fixImageEXIFOrientation(file);
    if (corrected) {
      file = corrected;
    }
  }

  const snapshot = await storageService.put(ref, file, (ev) => {
    cb((ev.bytesTransferred / ev.totalBytes) * 100, ev);
  });
  return snapshot;
}

export async function uploadFiles(
  files: FileList,
  ref: StorageReference,
  cb: (process: number, ev: UploadTaskSnapshot, path: string) => void,
  complete?: (ev: UploadTaskSnapshot, path: string) => void,
  error?: (_error: unknown, path: string) => void,
): Promise<void> {
  try {
    const allFiles = Object.entries(files).reduce<_FileList[]>(
      (fs, [path, file], key) => [
        ...fs,
        {
          path: `${path}.${key}`,
          file,
        },
      ],
      [],
    );

    while (allFiles.length) {
      const currentFile = allFiles.pop();
      if (currentFile && !isRemoteFileRef(currentFile.file)) {
        await _uploadFile(
          ref,
          fileSystem.getFileName(currentFile.file),
          currentFile.file,
          function processHandler(process, snapshot) {
            cb(process, snapshot, currentFile.path);
          },
        )
          .then(function successHandler(snapshot) {
            complete && complete(snapshot, currentFile.path);
            return;
          })
          .catch(function errorHandler(err) {
            error && error(err, currentFile.path);
            throw err;
          });
      }
    }

    return Promise.resolve();
  } catch (err) {
    return Promise.reject(err);
  }
}
